import { useState } from "react";
import { Link } from "react-router";
import { planLinkUpdatable, type Lesson, type LessonPlanChanges, type LessonPlanLink } from "@edu/shared";
import * as api from "../../lib/api.ts";
import {
  answersLossLabel,
  planClosedText,
  planDivergedText,
  startedText,
} from "../calls/call-row.ts";
import { lessonErrorText } from "./lesson-errors.ts";

/**
 * Связь урока с заготовкой в строке списка уроков.
 *
 * ⚠️ Состояние подгружается ПО КНОПКЕ, а не при отрисовке списка. Считается оно
 * по снимку доски, то есть по нескольким сотням килобайт jsonb на урок, и делать
 * это для двухсот строк сразу означало бы платить за то, чего человек не просил.
 *
 * Единственное, что показывается сразу, приезжает в самом списке
 * (`planMaterializedAt`): «материал не взят». Это ровно то состояние, которое
 * стоит увидеть, не нажимая ничего, потому что правки заготовки до такого урока
 * доезжают сами.
 *
 * Модуль разложен на три части, потому что колонка и кнопки живут в разных
 * местах строки: текст состояния стоит в слоте «Заготовка», а всё, что нажимают
 * (загрузка состояния, обновление, разбор расхождения), уехало в развёртку
 * строки. Общее состояние держит хук, иначе после обновления материала колонка
 * продолжала бы врать.
 */
export interface PlanLinkState {
  /** Заготовка урока. `null` и у собранного вручную, и у того, чей источник стёрли. */
  planId: string | null;
  /**
   * Материал уже взят.
   *
   * Отметку берём из загруженного состояния, если оно уже есть, и только потом
   * из списка. Список после обновления не перезапрашивается, и без этого строка
   * продолжала бы обещать «материал не взят» уже после того, как он взят.
   */
  taken: boolean;
  link: LessonPlanLink | null;
  loss: LessonPlanChanges | null;
  /**
   * Урок это звонок с преподавателями: ученика в нём нет, и слова про ученика
   * в отказах и в счёте потерь были бы неправдой (6.4
   * `docs/teacher-calls-design.md`).
   */
  call: boolean;
  busy: boolean;
  err: string | null;
  load: () => void;
  sync: (force: boolean) => void;
  dropLoss: () => void;
}

export function usePlanLink(lesson: Lesson): PlanLinkState {
  const call = lesson.call !== undefined;
  const [link, setLink] = useState<LessonPlanLink | null>(null);
  const [loss, setLoss] = useState<LessonPlanChanges | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run(fn: () => Promise<LessonPlanLink>) {
    setBusy(true);
    setErr(null);
    try {
      setLink(await fn());
      setLoss(null);
    } catch (e) {
      if (e instanceof api.ApiError && e.code === "board_diverged") {
        // Не ошибка, а вопрос: живая доска ушла от материала, и человек обязан
        // увидеть, что именно потеряет, до того как подтвердит
        setLoss(readChanges(e.details["changes"]));
        return;
      }
      setErr(syncErrorText(e, call));
    } finally {
      setBusy(false);
    }
  }

  return {
    planId: lesson.planId ?? null,
    taken: (link?.materializedAt ?? lesson.planMaterializedAt) != null,
    link,
    loss,
    call,
    busy,
    err,
    load: () => void run(() => api.getLessonPlanLink(lesson.id)),
    sync: (force: boolean) => void run(() => api.syncLessonPlan(lesson.id, force)),
    dropLoss: () => setLoss(null),
  };
}

/**
 * Слот «Заготовка». Занят всегда: у урока без заготовки в нём стоят слова про
 * её отсутствие, иначе колонки соседних строк разъезжаются.
 *
 * Текст сам себе ссылка на источник. Горячий путь из карты навигации дизайнера:
 * с урока в заготовку, из которой он собран, в один клик. Ссылкой прямо в
 * колонке, а не кнопкой в ряду: это переход, а не действие над уроком.
 *
 * ⚠️ `linkable` выключается у ученика. Заготовка ему не адресуема вовсе, все
 * ручки заготовок отвечают ему 404, и ссылка вела бы на отказ.
 */
export function PlanCell(props: { plan: PlanLinkState; linkable: boolean }) {
  const { planId, taken, link } = props.plan;

  if (planId === null) {
    // Заготовку стёрли, а материал урок взять успел. Связь слабая намеренно:
    // слайды у урока свои, и исчезновение источника он обязан пережить
    return taken ? (
      <span className="cell" data-label="Заготовка">
        заготовка удалена
      </span>
    ) : (
      <span className="cell row__void" data-label="Заготовка">
        без заготовки
      </span>
    );
  }

  const text = taken
    ? `по заготовке${link?.planTitle ? ` «${link.planTitle}»` : ""}`
    : "материал не взят";

  return (
    <span className="cell" data-label="Заготовка">
      {props.linkable ? (
        <Link className="textlink" to={`/plans/${planId}`} title="Открыть заготовку">
          {text}
        </Link>
      ) : (
        text
      )}
    </span>
  );
}

/** Есть ли что показывать в развёртке: у урока, собранного вручную, нечего. */
export function hasPlanTools(plan: PlanLinkState): boolean {
  return plan.planId !== null || plan.taken;
}

/**
 * Кнопки связи с заготовкой: развёртка строки.
 *
 * Здесь же живёт разбор расхождения и шаг подтверждения. Подтверждение
 * необратимого не обязано влезать в колонку шириной 10rem, ради этого развёртка
 * и заведена.
 */
export function PlanTools(props: { plan: PlanLinkState }) {
  const { link, loss, busy, err, taken } = props.plan;

  return (
    <>
      {link !== null && <p className="muted">{stateText(link, props.plan.call)}</p>}

      {err && <p className="err">{err}</p>}

      {loss !== null && (
        <p className="err">
          Обновление затрёт то, что на доске: {lossText(loss, props.plan.call)}. Обновить всё равно?
        </p>
      )}

      <div className="acts">
        {link === null && taken && (
          <button className="btn" disabled={busy} onClick={props.plan.load}>
            Материал
          </button>
        )}
        {loss !== null ? (
          <>
            {/*
              Заливка красным, а не обводка: обводка это «действие предлагается»,
              а здесь уже шаг подтверждения, и следующее нажатие затрёт правки на
              доске вместе с ответами ученика. Обычное «Обновить из заготовки»
              ниже красным не красится: там затирать нечего, расхождения нет
            */}
            <button
              className="btn btn--danger is-confirm"
              disabled={busy}
              onClick={() => props.plan.sync(true)}
            >
              Всё равно обновить
            </button>
            <button className="btn btn--quiet" disabled={busy} onClick={props.plan.dropLoss}>
              Отмена
            </button>
          </>
        ) : (
          canSync(link, taken) && (
            <button className="btn" disabled={busy} onClick={() => props.plan.sync(false)}>
              {taken ? "Обновить из заготовки" : "Взять материал сейчас"}
            </button>
          )
        )}
      </div>
    </>
  );
}

/**
 * Кнопки обновления нет у начатого урока: переписать проведённое занятие нельзя.
 * Правило «какой урок обновляем» одно с итогом дописывания страниц PDF
 * (`planLinkUpdatable`); `pending` («взять материал сейчас») добавляет экран.
 */
function canSync(link: LessonPlanLink | null, taken: boolean): boolean {
  if (link === null) return !taken;
  return link.state === "pending" || planLinkUpdatable(link.state);
}

function stateText(link: LessonPlanLink, call: boolean): string {
  const behind = link.behind ? ", заготовка новее" : "";
  switch (link.state) {
    case "pending":
      return "материал ещё не взят";
    case "clean":
      return `совпадает с заготовкой${behind}`;
    case "diverged":
      return `${planDivergedText(call)}${behind}`;
    case "closed":
      return planClosedText(call);
    default:
      return "без заготовки";
  }
}

function lossText(changes: LessonPlanChanges, call: boolean): string {
  const parts: string[] = [];
  if (changes.changedSlides.length > 0) {
    parts.push(`правки на слайдах ${changes.changedSlides.join(", ")}`);
  }
  if (changes.addedSlides > 0) parts.push(`добавленных слайдов ${changes.addedSlides}`);
  if (changes.removedSlides > 0) parts.push(`удалённых слайдов ${changes.removedSlides}`);
  if (changes.answers > 0) parts.push(`${answersLossLabel(call)} ${changes.answers}`);
  return parts.length === 0 ? "изменения на доске" : parts.join(", ");
}

/** Тело отказа читаем мягко: это подсказка человеку, а не контракт. */
function readChanges(raw: unknown): LessonPlanChanges | null {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Partial<LessonPlanChanges>;
  return {
    changedSlides: Array.isArray(value.changedSlides)
      ? value.changedSlides.filter((n): n is number => typeof n === "number")
      : [],
    addedSlides: typeof value.addedSlides === "number" ? value.addedSlides : 0,
    removedSlides: typeof value.removedSlides === "number" ? value.removedSlides : 0,
    answers: typeof value.answers === "number" ? value.answers : 0,
  };
}

function syncErrorText(e: unknown, call: boolean): string {
  if (!(e instanceof api.ApiError)) return e instanceof Error ? e.message : String(e);
  switch (e.code) {
    case "room_busy":
      return "В комнате сейчас кто-то есть. Обновление затёрло бы доску под ним";
    case "lesson_started":
      return startedText(call);
    case "lesson_without_plan":
      return "У урока нет заготовки";
    case "lesson_seed_failed":
      return "Сервер доски не ответил. Попробуйте ещё раз";
    default:
      return lessonErrorText(e);
  }
}
